import React, { FunctionComponent, useEffect, useState } from 'react';
import { cn } from '@companion-professional/webutils';
import { Label } from '../Label';
import { DiagnosisOption } from '../ImageDiagnosis';

export type EvaluationValue = string | number;

// EvaluationBaseProps is the base interface for the props used in the EvaluationSelector component.  This is also used
// by the MultipleEvaluations component.
export interface EvaluationBaseProps {
  options: DiagnosisOption[];
  disabled?: boolean;
  // minLabel and maxLabel are the labels for the min and max values of the pain scale. Defaults to 'none' and 'severe'
  minLabel?: string;
  maxLabel?: string;
}

interface EvaluationSelectorProps extends EvaluationBaseProps {
  name: string;
  label: string;
  description?: string;
  value?: EvaluationValue;
  onChange?: (value?: EvaluationValue) => void;
}

// EvaluationSelector displays a series of horizontally rendered selectable button like values that act like a radio
// selection.  This is used for things like pain selection from '-' to '+++'
export const EvaluationSelector: FunctionComponent<EvaluationSelectorProps> = ({
  name,
  label,
  description,
  options,
  onChange = () => {},
  value,
  disabled = false,
  minLabel = 'none',
  maxLabel = 'severe'
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    onChange(selectedValue);
  }, [selectedValue]);

  useEffect(() => {
    if (value !== selectedValue) {
      setSelectedValue(value);
    }
  }, [value]);

  return (
    <div className="mb-2 flex max-w-lg flex-col">
      <Label description={description}>{label}</Label>
      <div className="mt-2 flex flex-row gap-2">
        {options.map((option) => (
          <div
            key={option.label}
            data-testid={`evaluation-selector-${name}-${option.value}`}
            className={cn(
              'flex-1 cursor-pointer rounded-lg border border-input bg-widget py-2 text-center text-on-widget',
              {
                'font-semibold': selectedValue === option.value,
                'hover:opacity-70': !disabled
              }
            )}
            style={selectedValue === option.value ? { backgroundColor: option.color } : {}}
            onClick={() => {
              if (disabled) {
                return;
              }
              setSelectedValue(option.value);
            }}
          >
            {option.label}
          </div>
        ))}
      </div>
      <div className="mt-1 flex flex-row justify-between text-sm italic text-muted-foreground">
        <div>{minLabel}</div>
        <div>{maxLabel}</div>
      </div>
    </div>
  );
};
